import React, { useState, useEffect, useCallback } from 'react';
import './MoreInfo.css';
import { firestore, collection, getDocs, addDoc, deleteDoc, doc, updateDoc, getDoc, Timestamp } from './firebase-config';
import SignUpForm from './SignUpForm';
import GuestList from './GuestList';
import Prompts from './Prompts';
import QRCode from './QRCode';
import Select from 'react-select';
import Autocomplete from 'react-google-autocomplete';

const allZones = Intl.supportedValuesOf
  ? Intl.supportedValuesOf('timeZone')
  : ['UTC'];
const zoneOptions = allZones.map(z => ({ label: z, value: z }));
// below your component or in a utils file

// Turn a JS UTC Date into the "YYYY-MM-DDThh:mm" string that
// <input type="datetime-local"> expects, by formatting it in `zone`
function toInputValueInZone(date, zone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hourCycle: 'h23'
  });
  const parts = dtf.formatToParts(date).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/**
 * inputValue: "YYYY-MM-DDThh:mm"
 * zone: any IANA string
 * returns a JS Date in UTC corresponding to that wall-clock time in `zone`
 */
function parseDateInZone(inputValue, zone) {
  // split into date and time
  const [d, t] = (inputValue || '').split('T');
  if (!d || !t) return new Date(inputValue); // fallback

  const [year, month, day]   = d.split('-').map(Number);
  const [hour, minute]       = t.split(':').map(Number);

  // Build the UTC milli timestamp as if that wall-clock were UTC:
  const utcMs = Date.UTC(year, month - 1, day, hour, minute);

  // Ask Intl what the offset is *at that instant* in `zone`
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    timeZoneName: 'shortOffset'
  }).formatToParts(new Date(utcMs));

  const tzPart = parts.find(p => p.type === 'timeZoneName');
  const offsetStr = tzPart?.value || '';            // safe: never undefined

  // Try to pull out ±HH or ±HH:MM
  const m = offsetStr.match(/([+-]\d{1,2})(?::(\d{2}))?/);
  let totalMin = 0;
  if (m) {
    const sign = m[1][0] === '-' ? -1 : 1;
    const hOff = parseInt(m[1].slice(1), 10);
    const mOff = parseInt(m[2] || '0', 10);
    totalMin = sign * (hOff * 60 + mOff);
  }
  // Subtract that zone-offset (in minutes) to land on the true UTC epoch
  return new Date(utcMs - totalMin * 60_000);
}



function MoreInfo() {
  const [selectedZone, setSelectedZone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [eventCode, setEventCode] = useState('');
  const [isEventCodeValid, setIsEventCodeValid] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [itinerary, setItinerary] = useState([]);
  const [keyPeople, setKeyPeople] = useState([]);
  const [keyLocations, setKeyLocations] = useState([]);
  const [keyPlaylists, setPlaylists] = useState([]);
  const [activeTab, setActiveTab] = useState('itinerary');
  const [isSaving, setIsSaving] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [host, setHost] = useState(false); 
  const [user, setUser] = useState(null); 
  const [categoryFilter, setCategoryFilter] = useState('all');

  const handleEventCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    const eventDocRef = doc(firestore, 'events', eventCode);
    const eventDoc = await getDoc(eventDocRef);

    if (eventDoc.exists()) {
      const data = eventDoc.data();
      if (data.timeZone) {
        setSelectedZone(data.timeZone);
      }
      setIsEventCodeValid(true);
    } else {
      setErrorMessage('Invalid event code. Please try again.');
    }

    setLoading(false);
  };

  const handleZoneChange = opt => {
    setSelectedZone(opt.value);
  };
  
  const handleUserSignIn = async (user) => {
    // const user = auth.currentUser;
    setUser(user);

    if (!user) {
      setErrorMessage('You need to sign in.');
      return;
    }

    try {
      const userDocRef = doc(firestore, `events/${eventCode}/profiles`, user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        setHost(userData.host);
        await fetchEventData();
      } else {
        console.log('You are not registered for this event.');
      }
    } catch (error) {
      console.log('An error occurred while checking user registration.');
      console.error('Error:', error);
    }
  }; 

  const saveSectionToFirestore = async (sectionData, collectionPath) => {
    setIsSaving(true);
    try {
      const itemsToUpdate = collectionPath.includes("itinerary")
        ? sectionData.flatMap(([date, events]) => events)
        : sectionData;
  
      const promises = itemsToUpdate.map((item) => {
        if (!item.id) {
          console.error(`Skipping item without ID:`, item);
          return Promise.resolve(); // Skip invalid items
        }
  
        const updatedItem = {
          ...item,
          date: item.date ? Timestamp.fromDate(item.date) : null,
        };
  
        // console.log('Updating item:', { path: collectionPath, id: item.id, data: updatedItem });
  
        const docRef = doc(firestore, collectionPath, item.id);
        return updateDoc(docRef, updatedItem);
      });
  
      await Promise.all(promises);
      console.log(`${collectionPath} successfully updated!`);

      const eventRef = doc(firestore, 'events', eventCode);
      await updateDoc(eventRef, { timeZone: selectedZone });
      // console.log(`events/${eventCode} timeZone successfully updated!`);

    } catch (error) {
      console.error("Error updating section: ", error);
    } finally {
      await fetchEventData();
      setIsSaving(false);
    }
  };
  

  const addInputCard = async (type, defaultCategory) => {
    setIsAdding(true);
  
    const defaultValues = {
      itinerary: { 
        date: "", 
        eventName: "", 
        eventLocation: "", 
        eventAddress: "" },
      
      keyLocations: { 
        name: "", 
        address: "No Address", 
        details: defaultCategory},
      
      keyPeople: { 
        name: "", 
        role: "No Role", 
        phone: "", 
        email: "" },

      playlists: { 
        title: "No Title", 
        link: ""},
    };
  
    try {
      // Ensure eventCode is valid
      if (!eventCode) {
        console.error("Event code is not set.");
        return;
      }
  
      const collectionPath = `events/${eventCode}/${type}`;
      const newDocRef = await addDoc(collection(firestore, collectionPath), defaultValues[type]);
      console.log(`New ${type} document created with ID:`, newDocRef.id);
  
      // Update the local state with the new document
      const newItem = { id: newDocRef.id, ...defaultValues[type] };
  
      if (type === "itinerary") {
        setItinerary((prev) => {
          let noDateGroup = prev.find(([date]) => date === "No Date");
          if (!noDateGroup) {
            noDateGroup = ["No Date", []];
            prev.push(noDateGroup);
          }
          noDateGroup[1].push(newItem);
          return [...prev];
        });
      } else if (type === "keyLocations") {
        setKeyLocations((prev) => {
          const updatedLocations = [...prev, newItem];
          // Sort locations, ensuring "No Address" is at the bottom
          return updatedLocations.sort((a, b) => {
            if (a.address === "No Address") return 1;
            if (b.address === "No Address") return -1;
            return 0;
          });
        });
      } else if (type === "keyPeople") {
        setKeyPeople((prev) => {
          const updatedPeople = [...prev, newItem];
          // Sort people, ensuring "No Role" is at the bottom
          return updatedPeople.sort((a, b) => {
            if (a.role === "No Role") return 1;
            if (b.role === "No Role") return -1;
            return 0;
          });
        });
      } else if (type === "playlists") {
        setPlaylists((prev) => {
          const updatedPlaylists = [...prev, newItem];
          // Sort playlists, ensuring "No Title" is at the bottom
          return updatedPlaylists.sort((a, b) => {
            if (a.title === "No Title") return 1;
            if (b.title === "No Title") return -1;
            return 0;
          });
        });
      }
    } catch (error) {
      console.error(`Error creating new ${type} document:`, error);
    } finally {
      await fetchEventData();
      setIsAdding(false);
    }
  };

  const handleDeleteItem = async (id, type) => {
    try {
      // Define the collection path
      const collectionPath = `events/${eventCode}/${type}`;
  
      // Delete the document from Firestore
      await deleteDoc(doc(firestore, collectionPath, id));
  
      console.log(`${type} document with ID: ${id} deleted successfully`);
  
      // Update the local state
      if (type === "itinerary") {
        setItinerary((prev) =>
          prev.map(([date, events]) => [
            date,
            events.filter((event) => event.id !== id),
          ])
        );
      } else if (type === "keyLocations") {
        setKeyLocations((prev) => prev.filter((location) => location.id !== id));
      } else if (type === "keyPeople") {
        setKeyPeople((prev) => prev.filter((person) => person.id !== id));
      } else if (type === 'playlists') {
        setPlaylists(prev => prev.filter(p => p.id !== id));
      }
    } catch (error) {
      console.error(`Error deleting ${type} document:`, error);
    }
  };
  
  
  const fetchEventData = useCallback(async () => {
    // console.log("Fetching data...")
    try {
      const locationsPath = `events/${eventCode}/keyLocations`;
      const locationsRef = collection(firestore, locationsPath);
      const locationsSnapshot = await getDocs(locationsRef);
      const locationsData = locationsSnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name || '',
        address: doc.data().address || '',
        details: doc.data().details || '',
        date: doc.data().date ? new Date(doc.data().date.seconds * 1000) : null,
        description: doc.data().description || '',
      }));

      const sortedLocations = locationsData.sort((a, b) => {
        if (a.address === "No Address") return 1;
        if (b.address === "No Address") return -1;
        return 0;
      });
      setKeyLocations(sortedLocations || []);

      const peoplePath = `events/${eventCode  }/keyPeople`;
      const peopleRef = collection(firestore, peoplePath);
      const peopleSnapshot = await getDocs(peopleRef);
      const peopleData = peopleSnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name || '',
        role: doc.data().role || '',
        phone: doc.data().phone || '',
        email: doc.data().email || '',
      }));
      const sortedPeople = peopleData.sort((a, b) => {
        if (a.role === "No Role") return 1;
        if (b.role === "No Role") return -1;
        return 0;
      });
      setKeyPeople(sortedPeople || []);


      const playlistPath = `events/${eventCode}/playlists`;
      const playlistRef  = collection(firestore, playlistPath);
      const playlistSnapshot = await getDocs(playlistRef);
      const playlistData = playlistSnapshot.docs.map((doc) => ({
          id: doc.id,
          title: doc.data().title,
          link: doc.data().link,
      }));

      const sortedPlaylists = playlistData.sort((a, b) => {
        if (a.title === "No Title") return 1;
        if (b.title === "No Title") return -1;  
        return 0;
      });
      setPlaylists(sortedPlaylists || []);
    

      const itineraryPath = `events/${eventCode}/itinerary`;
      const itineraryRef = collection(firestore, itineraryPath);
      const itinerarySnapshot = await getDocs(itineraryRef);

      const groupedItinerary = {};
      itinerarySnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const eventDate = data.date ? new Date(data.date.seconds * 1000) : null;

        const formattedDate = eventDate
          ? eventDate.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })
          : 'No Date';

        const formattedTime = eventDate
          ? eventDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          : 'No time';
        
        if (!groupedItinerary[formattedDate]) {
          groupedItinerary[formattedDate] = [];
        }

        groupedItinerary[formattedDate].push({
          id: doc.id,
          eventName: data.eventName || 'No title',
          eventLocation: data.eventLocation || 'Unknown Location',
          eventAddress: data.eventAddress,
          time: formattedTime,
          // contactName: peopleData.find((person) => person.id === data.contact)?.name || '',
          // contactPhone: peopleData.find((person) => person.id === data.contact)?.phone || '',
          date: eventDate,
        });

        //console.log(groupedItinerary);
      });

      // Sort grouped itinerary by date, placing "No Date" last
      // 1) Sort each day’s events by their Date
      Object.entries(groupedItinerary).forEach(([label, events]) => {
        events.sort((a, b) => a.date - b.date);
      });

      // 2) Build an array [[label, events], …] and sort the days by the first event’s date
      const sortedGroupedItinerary = Object
        .entries(groupedItinerary)
        // place the “No Date” bucket last, if you still need it:
        .sort(([labA, evA], [labB, evB]) => {
          if (labA === "No Date") return 1;
          if (labB === "No Date") return -1;
          // otherwise compare the real Date on the first event:
          return evA[0].date - evB[0].date;
        });

      setItinerary(sortedGroupedItinerary || []);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [eventCode]);

  const renderEditableDateTime = (date, onChange) => {
    // only compute once, as the default value
    const initial = date
      ? toInputValueInZone(date, selectedZone)
      : '';

    return (
      <input
        type="datetime-local"
        defaultValue={initial}
        onBlur={e => {
          const v = e.target.value;
          // only commit if it's fully formed
          if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) {
            const newUtc = parseDateInZone(v, selectedZone);
            onChange(newUtc);
          }
        }}
        className="editable-input"
      />
    );
  };

  const renderEditable = (value, onChange, placeholder, type) => (
    <div className="edit-input-field">
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="editable-input"
        placeholder={placeholder}
      />
    </div>
  );

  const renderItinerary = () => (
    <div className="itinerary-container">
      <h1>Itinerary</h1>
      <div className="timezone-picker">
        <Select
          isSearchable
          options={zoneOptions}
          value={zoneOptions.find(z => z.value === selectedZone)}
          onChange={handleZoneChange}
          placeholder="Pick time zone…"
          className="timezone-select"
        />
      </div>
      {itinerary.length > 0 ? (
        itinerary.map(([date, events], index) => (
          <div className="day-section" key={index}>
            <h2>{date}</h2>
            <div className="events-container">
              {events.map((event, idx) => (
                <div className="event-card" key={idx}>
                  <div className="event-time">
                    <span className="material-icons-outlined">schedule</span>
                    {renderEditableDateTime(event.date, (newDate) =>
                      setItinerary((prev) => {
                        const updatedItinerary = [...prev];
                        updatedItinerary[index][1][idx].date = newDate; // Update the date
                        return updatedItinerary;
                      })
                    )}
                  </div>
                  <div className="event-details">
                    <div>
                    <span className="material-icons-outlined">local_activity</span>
                      {renderEditable(event.eventName, (value) =>
                        setItinerary((prev) => {
                          const newItinerary = [...prev];
                          newItinerary[index][1][idx].eventName = value;
                          return newItinerary;
                        }),
                      "Enter event name", "text"
                      )}
                    </div>
                    <div>
                      <span className="material-icons-outlined">villa</span>
                      {renderEditable(event.eventLocation, (value) =>
                        setItinerary((prev) => {
                          const newItinerary = [...prev];
                          newItinerary[index][1][idx].eventLocation = value;
                          return newItinerary;
                        }),
                      "Enter event location name", "text"
                      )}
                    </div>
                    <div>  
                      <span className="material-icons-outlined">location_on</span>
                      {/* {renderEditable(event.eventAddress, (value) =>
                        setItinerary((prev) => {
                          const newItinerary = [...prev];
                          newItinerary[index][1][idx].eventAddress = value;
                          return newItinerary;
                        }),
                      "Enter event location address", "text"
                      )} */}
                      <Autocomplete
                        apiKey="AIzaSyASlGwATHUDB5OchvlUQjIyduxmUSL2AxY"
                        defaultValue={event.eventAddress}
                        onPlaceSelected={(place) => {
                          const formattedAddress = place.formatted_address || place.name;
                          const placeId = place.place_id;
                          setItinerary((prev) => {
                            const newItinerary = [...prev];
                            newItinerary[index][1][idx].eventAddress = formattedAddress;
                            newItinerary[index][1][idx].placeId = placeId;
                            return newItinerary;
                          });
                        }}
                        options={{
                          types: ['establishment', 'geocode']
                        }}
                        className="autocomplete-input"
                      />
                    </div>
                  </div>
                  <button 
                    className="delete-button" 
                    onClick={() => handleDeleteItem(event.id, "itinerary")}>
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))
      ) : (
        <div>No itinerary data available.</div>
      )}
      <button className="add-button" onClick={() => addInputCard("itinerary")} disabled={isAdding}>
        <span className="material-icons-outlined">add</span>
      </button>
      <button className="save-button" onClick={() => saveSectionToFirestore(itinerary, `events/${eventCode}/itinerary`)} disabled={isSaving}>
        {isSaving ? "Saving..." : "Save"}
      </button>
    </div>
  );

  const renderKeyPeople = () => (
    <div className="key-people-container">
      <h1>Key People</h1>
      {keyPeople.map((person, index) => (
        <div className="person-card" key={index}>
          <div>
          <span className="material-icons-outlined">person</span>
          {renderEditable(person.name, (value) =>
              setKeyPeople((prev) => {
                const newPeople = [...prev];
                newPeople[index].name = value;
                return newPeople;
              }),
              "Enter contact name", "text"
            )}
          </div>
          <div>
          <span className="material-icons-outlined">contact_page</span>
          {renderEditable(person.role, (value) =>
              setKeyPeople((prev) => {
                const newPeople = [...prev];
                newPeople[index].role = value;
                return newPeople;
              }),
              "Enter contact role", "text"
            )}
          </div>
          <div>
            <span className="material-icons-outlined">contact_phone</span>
            {renderEditable(person.phone, (value) =>
              setKeyPeople((prev) => {
                const newPeople = [...prev];
                newPeople[index].phone = value;
                return newPeople;
              }),
              "Enter contact phone", "phone"
            )}
          </div>
          <div>
            <span className="material-icons-outlined">email</span>
            {renderEditable(person.email, (value) =>
              setKeyPeople((prev) => {
                const newPeople = [...prev];
                newPeople[index].email = value;
                return newPeople;
              }),
              "Enter contact email", "email"
            )}
          </div>
          <button 
            className="delete-button" 
            onClick={() => handleDeleteItem(person.id, "keyPeople")}>
            Delete
          </button>
        </div>
      ))}
      <button className="add-button" onClick={() => addInputCard("keyPeople")} disabled={isAdding}>
        <span className="material-icons-outlined">add</span>
      </button>
      <button className="save-button" onClick={() => saveSectionToFirestore(keyPeople, `events/${eventCode}/keyPeople`)} disabled={isSaving}>
        {isSaving ? "Saving..." : "Save"}
      </button>
    </div>
  );

  const renderKeyLocations = () => {
    const CAT = [
      { key: 'all',        label: 'All' },
      { key: 'Restaurant', label: 'Restaurants' },
      { key: 'Bar',        label: 'Bars' },
      { key: 'Coffee Shop', label: 'Coffee Shops' },
      { key: 'Activity',   label: 'Activities' },
      { key: 'Salon',   label: 'Salons' },
      { key: 'Grocery Store',   label: 'Grocery Stores' },
    ];
    const shown = keyLocations.filter(loc =>
      categoryFilter === 'all'
        ? true
        : loc.details?.toLowerCase().includes(categoryFilter.toLowerCase())
    );
    return (
    <div className="key-locations-container">
      <h1>Recommended Locations</h1>
      
      <div className="itinerary-filter-bar">
        {CAT.map(c => (
          <button
            key={c.key}
            onClick={() => setCategoryFilter(c.key)}
            className={categoryFilter === c.key ? 'active' : ''}
          >
            {c.label}
          </button>
        ))}
      </div>

      {shown.map(location => (
        <div className="location-card" key={location.id}>
          <div>
          <span className="material-icons-outlined">map</span>
            {renderEditable(location.name, (value) =>
              setKeyLocations(prev =>
                prev.map(loc =>
                  loc.id === location.id
                    ? { ...loc, name: value }
                    : loc
                )
              ),
              "Enter location name", "text"
            )}
          </div>
          <div>
            <span className="material-icons-outlined">location_on</span>
            {/* {renderEditable(location.address, (value) =>
              setKeyLocations(prev =>
                prev.map(loc =>
                  loc.id === location.id
                    ? { ...loc, address: value }
                    : loc
                )
              ),
              "Enter location address", "address"
            )} */}
            <Autocomplete
              apiKey="AIzaSyASlGwATHUDB5OchvlUQjIyduxmUSL2AxY"
              defaultValue={location.address}
              onPlaceSelected={(place) => {
                const formattedAddress = place.formatted_address || place.name;
                const placeId = place.place_id;

                setKeyLocations(prev =>
                  prev.map(loc =>
                    loc.id === location.id
                      ? { ...loc, address: formattedAddress, placeId }
                      : loc
                  )
                );
              }}
              options={{
                types: ['establishment', 'geocode']
              }}
              className="autocomplete-input"
            />
          </div>

          <div>
            <span className="material-icons-outlined">notes</span>
            <textarea
              value={location.description}
              onChange={e => {
                const newDesc = e.target.value;
                setKeyLocations(prev =>
                  prev.map(loc =>
                    loc.id === location.id
                      ? { ...loc, description: newDesc }
                      : loc
                  )
                );
              }}
              placeholder="Optional description"
              className="editable-input"
              rows={2}
            />
          </div>

          <div>
            <span className="material-icons-outlined">description</span>
            <select
              value={location.details}
              onChange={e => {
                const newCat = e.target.value;
                setKeyLocations(prev =>
                  prev.map(loc =>
                    loc.id === location.id
                      ? { ...loc, details: newCat }
                      : loc
                  )
                );
              }}
            >
              <option value="" disabled>Choose category...</option>
              {CAT.map(({key,label}) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          {/* <div>
            <span className="material-icons-outlined">description</span>
            {renderEditable(location.details, (value) =>
              setKeyLocations((prev) => {
                const newLocations = [...prev];
                newLocations[index].details = value;
                return newLocations;
              }),
                "Enter location details", "text"
            )}
          </div> */}
          <button 
            className="delete-button" 
            onClick={() => handleDeleteItem(location.id, "keyLocations")}>
            Delete
          </button>
        </div>
      ))}
      <button className="add-button" onClick={() => addInputCard("keyLocations", categoryFilter)} disabled={isAdding}>
        <span className="material-icons-outlined">add</span>
      </button>
      <button className="save-button" onClick={() => saveSectionToFirestore(keyLocations, `events/${eventCode}/keyLocations`)} disabled={isSaving}>
        {isSaving ? "Saving..." : "Save"}
      </button>
    </div>
    );
  };

  const renderSpotify = () => (
    <div className="key-locations-container">
      <h1>Spotify Playlists</h1>
  
      {keyPlaylists.map((pl, index) => (
        <div className="location-card" key={pl.id ?? index}>
          <div>
            <span className="material-icons-outlined">queue_music</span>
            {renderEditable(
              pl.title,
              (value) =>
                setPlaylists((prev) => {
                  const next = [...prev];
                  next[index].title = value;
                  return next;
                }),
              'Playlist Title',
              'text'
            )}
          </div>
  
          <div>
            <span className="material-icons-outlined">link</span>
            {renderEditable(
              pl.link,
              (value) =>
                setPlaylists((prev) => {
                  const next = [...prev];
                  next[index].link = value;
                  return next;
                }),
              'Spotify Playlist Link',
              'url'
            )}
          </div>
  
          <button className="delete-button" onClick={() => handleDeleteItem(pl.id, 'playlists')}>
            Delete
          </button>
        </div>
      ))}
  
      <button className="add-button" onClick={() => addInputCard('playlists')} disabled={isAdding}>
        <span className="material-icons-outlined">add</span>
      </button>
  
      <button className="save-button" onClick={() => saveSectionToFirestore(keyPlaylists, `events/${eventCode}/playlists`)}
        disabled={isSaving}
      >
        {isSaving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
  
  useEffect(() => {
    if (!eventCode) return;
    fetchEventData();
  }, [activeTab, eventCode, fetchEventData]);

  return (
    <section id="features-moreinfo">
      {!isEventCodeValid ? (
      <div className="enter-event-code">
        <h2>Enter Event Code</h2>
        <form onSubmit={handleEventCodeSubmit}>
          <input
            type="text" 
            value={eventCode}
            onChange={(e) => {
              setEventCode(e.target.value);
              setErrorMessage('');
            }}
            placeholder="Enter your event code"
          />
          <button className="event-code-submit-button" type="submit" disabled={loading}>
            <span className="material-icons">send</span>
            {loading ? 'Verifying...' : ''}
          </button>
        </form>
        {errorMessage && <p className="error-message">{errorMessage}</p>}
      </div>
      ) : !user ? (
        <SignUpForm eventCode={eventCode} onSignUpSuccess={handleUserSignIn}/>
      ) : host ? (
        <div className="itinerary-page">
          <div className="tabs-itinerary">
            <button className={`tab-button-itinerary ${activeTab === 'itinerary' ? 'active' : ''}`} 
              onClick={() => setActiveTab('itinerary')}>
              <span className={activeTab === 'itinerary' ? 'material-icons' : 'material-icons-outlined'}>calendar_today</span>              
              {/* Itinerary */}
            </button>
            <button className={`tab-button-itinerary ${activeTab === 'people' ? 'active' : ''}`} 
              onClick={() => setActiveTab('people')}>
              <span className={activeTab === 'people' ? 'material-icons' : 'material-icons-outlined'}>contact_phone</span>              
              {/* Contacts */}
            </button>
            <button className={`tab-button-itinerary ${activeTab === 'locations' ? 'active' : ''}`} 
              onClick={() => setActiveTab('locations')}>
              <span className={activeTab === 'locations' ? 'material-icons' : 'material-icons-outlined'}>place</span>              
              {/* Locations */}
            </button>
            <button className={`tab-button-itinerary ${activeTab === 'guestList' ? 'active' : ''}`} 
              onClick={() => setActiveTab('guestList')}>
              <span className={activeTab === 'guestList' ? 'material-icons' : 'material-icons-outlined'}>people</span>              
              {/* Guest List */}
            </button>
            <button className={`tab-button-itinerary ${activeTab === 'prompts' ? 'active' : ''}`} 
              onClick={() => setActiveTab('prompts')}>
              <span className="material-icons">{activeTab === 'prompts' ? 'chat_bubble' : 'chat_bubble_outline'}</span>
              {/* Prompts */}
            </button>
            <button className={`tab-button-itinerary ${activeTab === 'QRCode' ? 'active' : ''}`} 
              onClick={() => setActiveTab('QRCode')}>
              <span className="material-icons">{activeTab === 'QRCode' ? 'qr_code_2' : 'qr_code'}</span>
              {/* Playlists */}
            </button>
            <button className={`tab-button-itinerary ${activeTab === 'spotify' ? 'active' : ''}`} 
              onClick={() => setActiveTab('spotify')}>
            <span className={activeTab === 'spotify' ? 'material-icons' : 'material-icons-outlined'}>play_circle</span>              
        </button>
          </div>
          <div className="itinerary-content">
            {activeTab === 'itinerary' && renderItinerary()}
            {activeTab === 'people' && renderKeyPeople()}
            {activeTab === 'locations' && renderKeyLocations()}
            {activeTab === 'eventhub' && renderItinerary()}
            {activeTab === "guestList" && <GuestList eventCode={eventCode} />}
            {activeTab === "prompts" && <Prompts eventCode={eventCode} />}
            {activeTab === "QRCode" && <QRCode eventCode={eventCode} />}
            {activeTab === "spotify" && renderSpotify()}
            </div>
        </div>
      ) : (
        <div>Welcome, user! You are not a host for this event.</div>
      )}
    </section>
  );
}

export default MoreInfo;
